import React from 'react';

const Testimonials: React.FC = () => {
  const testimonials = [
    {
      quote: "Crel AI completely transformed our customer service overnight. Response times improved by 80% and customer satisfaction scores reached an all-time high. It's like having a team of expert agents available 24/7.",
      author: "Sarah Chen",
      title: "VP of Customer Success",
      company: "TechFlow Inc.",
      avatar: "SC"
    },
    {
      quote: "The natural conversation flow is absolutely incredible. Our customers genuinely can't tell they're talking to an AI agent. It's revolutionized how we handle support and freed up our team for strategic work.",
      author: "Michael Rodriguez", 
      title: "Head of Operations",
      company: "CloudScale Solutions",
      avatar: "MR"
    },
    {
      quote: "We reduced our support costs by 60% while dramatically improving service quality. Crel AI paid for itself within the first month of implementation. The ROI is simply outstanding.",
      author: "Emily Thompson",
      title: "Chief Technology Officer", 
      company: "DataVault Systems",
      avatar: "ET"
    }
  ];

  return (
    <section id="testimonials" className="py-32 px-4 sm:px-6 lg:px-8 section-dark relative overflow-hidden">
      {/* Background Effects */}
      <div className="floating-orb w-96 h-96 bg-pink-500 top-20 left-20"></div>
      <div className="floating-orb w-80 h-80 bg-blue-500 bottom-40 right-10"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 rounded-full glass-effect text-white text-sm font-medium mb-8">
            <span className="gradient-text font-semibold">Customer Success Stories</span>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8 leading-tight">
            Trusted by{' '}
            <span className="gradient-text">Industry Leaders</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            See how companies across industries are transforming their customer service 
            with Crel AI voice agents and achieving unprecedented results.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-32">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="glass-card p-8 rounded-3xl card-hover relative overflow-hidden"
            >
              {/* Gradient Border Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                {/* Quote Icon */}
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </div>

                {/* Quote */}
                <blockquote className="text-gray-300 text-lg leading-relaxed mb-8">
                  "{testimonial.quote}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-bold text-white text-lg">{testimonial.author}</div>
                    <div className="text-sm text-gray-400">{testimonial.title}</div>
                    <div className="text-sm gradient-text font-semibold">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="glass-card rounded-3xl p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
          
          <div className="relative z-10 text-center mb-12">
            <h3 className="text-4xl font-black text-white mb-4">
              Proven Results <span className="gradient-text">Across Industries</span>
            </h3>
            <p className="text-xl text-gray-300">
              Join thousands of companies already transforming their customer service
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-black gradient-text mb-2">10,000+</div>
              <div className="text-gray-400 font-medium">Active Deployments</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black gradient-text mb-2">99.8%</div>
              <div className="text-gray-400 font-medium">Customer Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black gradient-text mb-2">50M+</div>
              <div className="text-gray-400 font-medium">Conversations Handled</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black gradient-text mb-2">60%</div>
              <div className="text-gray-400 font-medium">Average Cost Savings</div>
            </div>
          </div>
        </div>

        {/* Company Logos */}
        <div className="mt-20">
          <p className="text-center text-gray-500 mb-12 text-lg">Trusted by leading companies worldwide</p>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
            {/* Placeholder for company logos */}
            <div className="glass-effect rounded-xl p-6 hover:opacity-100 transition-opacity duration-300">
              <span className="text-gray-400 font-semibold text-lg">TechFlow</span>
            </div>
            <div className="glass-effect rounded-xl p-6 hover:opacity-100 transition-opacity duration-300">
              <span className="text-gray-400 font-semibold text-lg">CloudScale</span>
            </div>
            <div className="glass-effect rounded-xl p-6 hover:opacity-100 transition-opacity duration-300">
              <span className="text-gray-400 font-semibold text-lg">DataVault</span>
            </div>
            <div className="glass-effect rounded-xl p-6 hover:opacity-100 transition-opacity duration-300">
              <span className="text-gray-400 font-semibold text-lg">InnovateCorp</span>
            </div>
            <div className="glass-effect rounded-xl p-6 hover:opacity-100 transition-opacity duration-300">
              <span className="text-gray-400 font-semibold text-lg">FutureTech</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials; 